import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Grid, Card, CardContent, Typography, TextField, Select, MenuItem, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import OvalSpinner from '../components/UI/Spinners/OvalSpinner';
import AdminSideMenu from '../components/UI/AdminSideMenu/AdminSideMenu';
import styles from './Analytics.module.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { useErrorSetter } from '../hooks/useUiMessages';
import { checkAuthenticatedAdmin } from '../utils/client-functions';

const AdminsDashboard = () => {
  const token = sessionStorage?.getItem('afd8TvhsdjwiuuvsgjhsAfgsUhjs');
  const navigate = useNavigate();
  const location = useLocation();
  const setError = useErrorSetter();

  const [admins, setAdmins] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ filteredTotal: 0, roles: {}, newInRange: 0 });

  const [query, setQuery] = useState('');
  const [role, setRole] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({ fullName: '', adminEmail: '', role: 'admin', password: '' });
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    // Fetch current admin for role-based guard
    (async () => {
      try {
        const resp = await axios.get('/origin/get-current-admin', { headers: { 'x-access-token': token } });
        setCurrentAdmin(resp?.data || null);
      } catch {}
    })();
    const params = new URLSearchParams(location.search);
    setPage(parseInt(params.get('page') || '1', 10));
    setLimit(parseInt(params.get('limit') || '50', 10));
    setQuery(params.get('q') || '');
    setRole(params.get('role') || '');
    setStartDate(params.get('startDate') || '');
    setEndDate(params.get('endDate') || '');
    setSortBy(params.get('sortBy') || 'createdAt');
    setSortDir(params.get('sortDir') || 'desc');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (query) params.set('q', query);
    if (role) params.set('role', role);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (sortBy) params.set('sortBy', sortBy);
    if (sortDir) params.set('sortDir', sortDir);
    const newUrl = `${location.pathname}?${params.toString()}`;
    if (newUrl !== window.location.pathname + window.location.search) {
      window.history.replaceState(null, '', newUrl);
    }
  }, [page, limit, query, role, startDate, endDate, sortBy, sortDir, location.pathname]);

  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        await checkAuthenticatedAdmin();
      } catch {
        return navigate('/');
      }
      try {
        setLoading(true);
        const p = new URLSearchParams();
        p.set('page', String(page));
        p.set('limit', String(limit));
        if (query) p.set('q', query);
        if (role) p.set('role', role);
        if (startDate) p.set('startDate', startDate);
        if (endDate) p.set('endDate', endDate);
        if (sortBy) p.set('sortBy', sortBy);
        if (sortDir) p.set('sortDir', sortDir);
        const resp = await axios.get(`/origin/get-all-admins?${p.toString()}`, { headers: { 'x-access-token': token } });
        if (resp.status === 200) {
          setAdmins(resp.data.admins || []);
          setTotal(resp.data.total || 0);
        }
      } catch (e) {
        setError(e?.response?.data?.message || 'Failed to fetch admins');
      } finally {
        setLoading(false);
      }
    };
    fetchAdmins();
  }, [page, limit, query, role, startDate, endDate, sortBy, sortDir, token, navigate, setError]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const p = new URLSearchParams();
        if (query) p.set('q', query);
        if (role) p.set('role', role);
        if (startDate) p.set('startDate', startDate);
        if (endDate) p.set('endDate', endDate);
        const resp = await axios.get(`/origin/get-admins-stats?${p.toString()}`, { headers: { 'x-access-token': token } });
        setStats(resp?.data || { filteredTotal: 0, roles: {}, newInRange: 0 });
      } catch {}
    };
    fetchStats();
  }, [query, role, startDate, endDate, token]);

  const onSortChange = (key) => {
    setSortDir((prev) => (sortBy === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortBy(key);
    setPage(1);
  };

  const resetFilters = () => {
    setQuery('');
    setRole('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const exportCSV = () => {
    const cols = ['email','fullName','role','createdBy','createdAt'];
    const escapeCell = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? '"' + s + '"' : s;
    };
    const header = cols.join(',');
    const rows = admins.map(a => cols.map(c => escapeCell(a[c])).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admins_page${page}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const submitCreateAdmin = async () => {
    try {
      setSubmitLoading(true);
      if (!form.adminEmail || !form.fullName || !form.password || !form.role) {
        setSubmitLoading(false);
        return setError('Please fill all fields');
      }
      const resp = await axios.post('/origin/create-admin', form, { headers: { 'x-access-token': token } });
      if (resp.status === 201) {
        setDialogOpen(false);
        setForm({ fullName: '', adminEmail: '', role: 'admin', password: '' });
        // refresh
        const p = new URLSearchParams(window.location.search);
        const listResp = await axios.get(`/origin/get-all-admins?${p.toString()}`, { headers: { 'x-access-token': token } });
        setAdmins(listResp?.data?.admins || []);
        setTotal(listResp?.data?.total || 0);
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to create admin');
    } finally {
      setSubmitLoading(false);
    }
  };

  const startEdit = (adm) => {
    setEditMode(true);
    setForm({ adminEmail: adm.email, fullName: adm.fullName, role: adm.role, password: '' });
    setDialogOpen(true);
  };

  const submitEditAdmin = async () => {
    try {
      setSubmitLoading(true);
      if (!form.adminEmail || !form.fullName || !form.role) return setError('Please fill all required fields');
      const target = admins.find(a => a.email === form.adminEmail);
      if (!target) return setError('Admin not found');
      const payload = { adminId: target._id, fullName: form.fullName, role: form.role };
      if (form.password) payload.password = form.password;
      const resp = await axios.post('/origin/update-admin', payload, { headers: { 'x-access-token': token } });
      if (resp.status === 200) {
        setDialogOpen(false);
        setEditMode(false);
        setForm({ fullName: '', adminEmail: '', role: 'admin', password: '' });
        const p = new URLSearchParams(window.location.search);
        const listResp = await axios.get(`/origin/get-all-admins?${p.toString()}`, { headers: { 'x-access-token': token } });
        setAdmins(listResp?.data?.admins || []);
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to update admin');
    } finally {
      setSubmitLoading(false);
    }
  };

  const deleteAdmin = async (adm) => {
    try {
      if (!window.confirm(`Delete admin ${adm.email}?`)) return;
      const resp = await axios.post('/origin/delete-admin', { adminId: adm._id }, { headers: { 'x-access-token': token } });
      if (resp.status === 200) {
        setAdmins(prev => prev.filter(a => a._id !== adm._id));
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to delete admin');
    }
  };

  return (
    <div className={styles.analyticsContainer}>
      <AdminSideMenu />
      <div className={styles.analyticsContent}>
        <div className={styles.analyticsHeader}>
          <div className={styles.headerLeft}>
            <Typography variant="h4" className={styles.pageTitle}>Admin Settings</Typography>
            <Typography variant="body2" className={styles.pageSubtitle}>Manage admins, roles, and access</Typography>
          </div>
        </div>

        <Box sx={{ background: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', p: 2, mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField size="small" label="Search (email/name/creator)" value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }} />
          <Select size="small" value={role} displayEmpty onChange={(e) => { setRole(e.target.value); setPage(1); }}>
            <MenuItem value=""><em>Role: Any</em></MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="superadmin">Superadmin</MenuItem>
            <MenuItem value="support">Support</MenuItem>
          </Select>
          <TextField size="small" type="date" label="Start" InputLabelProps={{ shrink: true }} value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} />
          <TextField size="small" type="date" label="End" InputLabelProps={{ shrink: true }} value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} />
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={resetFilters}>Reset</Button>
            {currentAdmin?.role === 'superadmin' && (
              <Button variant="outlined" onClick={() => setDialogOpen(true)}>Create Admin</Button>
            )}
            {currentAdmin?.role === 'superadmin' && (
              <Button variant="contained" onClick={exportCSV}>Export CSV</Button>
            )}
          </Box>
        </Box>

        <Grid container spacing={2} className={styles.kpiContainer}>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
              <CardContent>
                <Typography className={styles.kpiLabel}>Total (filtered)</Typography>
                <Typography className={styles.kpiValue}>{stats.filteredTotal || total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
              <CardContent>
                <Typography className={styles.kpiLabel}>Roles</Typography>
                <Typography className={styles.kpiValue}>{Object.entries(stats.roles || {}).map(([k,v]) => `${k}:${v}`).join('  ') || '—'}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
              <CardContent>
                <Typography className={styles.kpiLabel}>New (range)</Typography>
                <Typography className={styles.kpiValue}>{stats.newInRange || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box className={styles.chartContainer} sx={{ p: 0 }}>
          <AdminsTable admins={admins} sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} onEdit={startEdit} onDelete={deleteAdmin} canManage={currentAdmin?.role === 'superadmin'} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, justifyContent: 'flex-end' }}>
          <Button disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
          <Typography variant="body2">Page {page}</Typography>
          <Button disabled={page * limit >= total || loading} onClick={() => setPage((p) => p + 1)}>Next</Button>
          <Select size="small" value={String(limit)} onChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(1); }}>
            {[25, 50, 100, 200].map(n => <MenuItem key={n} value={String(n)}>{n}/page</MenuItem>)}
          </Select>
        </Box>

        <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setEditMode(false); }}>
          <DialogTitle>{editMode ? 'Edit Admin' : 'Create Admin'}</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Full Name" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
            <TextField label="Email" type="email" value={form.adminEmail} disabled={editMode} onChange={(e) => setForm({ ...form, adminEmail: e.target.value })} />
            <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="superadmin">Superadmin</MenuItem>
              <MenuItem value="support">Support</MenuItem>
            </Select>
            <TextField label="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setDialogOpen(false); setEditMode(false); }}>Cancel</Button>
            {editMode ? (
              <Button variant="contained" onClick={submitEditAdmin} disabled={submitLoading}>
                {submitLoading ? <OvalSpinner size={18} color="#fff" /> : 'Save'}
              </Button>
            ) : (
              <Button variant="contained" onClick={submitCreateAdmin} disabled={currentAdmin?.role !== 'superadmin' || submitLoading}>
                {submitLoading ? <OvalSpinner size={18} color="#fff" /> : 'Create'}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

const AdminsTable = ({ admins, sortBy, sortDir, onSortChange, onEdit, onDelete, canManage }) => {
  const cols = [
    { key: 'email', label: 'Email', width: 260 },
    { key: 'fullName', label: 'Full Name', width: 200 },
    { key: 'role', label: 'Role', width: 120 },
    { key: 'createdBy', label: 'Created By', width: 220 },
    { key: 'createdAt', label: 'Created', width: 180 },
  ];
  const arrow = (k) => (sortBy === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');
  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'separate' }}>
        <thead>
          <tr>
            {cols.map(c => (
              <th key={c.key} style={{ textAlign: 'left', background: '#c0d1d457', padding: '12px', cursor: 'pointer', width: c.width }} onClick={() => onSortChange && onSortChange(c.key)}>
                {c.label}{arrow(c.key)}
              </th>
            ))}
            {canManage && <th style={{ background: '#c0d1d457', padding: '12px', width: 120 }}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {admins.map((a, idx) => (
            <tr key={a._id || idx}>
              {cols.map(c => (
                <td key={c.key} style={{ padding: '10px' }}>{String(a[c.key] ?? '')}</td>
              ))}
              {canManage && (
                <td style={{ padding: '10px' }}>
                  <button onClick={() => onEdit && onEdit(a)} style={{ marginRight: 8 }}>Edit</button>
                  <button onClick={() => onDelete && onDelete(a)} style={{ color: '#b00020' }}>Delete</button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
};

export default AdminsDashboard;
